import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { memberTabAdminActionClick } from '@wix/bi-logger-groups/v2';

import { IGroupMember } from 'api/members/types';
import { Role as GroupRole } from '@wix/ambassador-social-groups-v2-group-role/types';
import { selectCurrentUser, selectHasAdminRole } from 'store/selectors';

import { More as MoreIcon } from '@wix/wix-ui-icons-common/on-stage';
import { Menu, MenuItem } from 'wui/Menu';
import { Hide } from 'wui/Hide';
import { Show } from 'wui/Show';
import { Skeleton } from 'wui/Skeleton';
import { ListItem } from 'wui/ListItem';
import { ListItemAvatar } from 'wui/ListItemAvatar';
import { ListItemText } from 'wui/ListItemText';
import { IconButton } from 'wui/IconButton';

import {
  FollowButton,
  ProfileListItem,
} from 'common/components/ProfileListItem';

import { RemoveMemberDialog } from './RemoveMemberDialog';
import { MemberAnswersDialog } from './MemberAnswersDialog';
import { PromoteMemberDialog } from './PromoteMemberDialog';

interface IMemberProps {
  groupId: string;
  member: IGroupMember;
}

export function MemberListItem(props: IMemberProps) {
  const { member, groupId } = props;

  const { t } = useTranslation();
  const bi = useBi();
  const { isMobile } = useEnvironment();
  const user = useSelector(selectCurrentUser);
  const isAdmin = useSelector(selectHasAdminRole(groupId));

  const [dialogs, setDialogs] = useState({
    answers: false,
    remove: false,
    changeRole: false,
  });

  const isMemberAdmin = member.role === GroupRole.ADMIN;
  const showActions = isAdmin && user.siteMemberId !== member.siteMemberId;

  return (
    <>
      <ProfileListItem
        wired
        disablePadding
        profile={member}
        avatarProps={{ size: isMobile ? 'medium' : 'large' }}
      >
        <Hide if={isMobile}>
          <FollowButton groupId={groupId} profile={member} />
        </Hide>
        <Show if={showActions}>
          <Menu
            element={
              <IconButton
                icon={<MoreIcon />}
                aria-label={t('groups-web.a11y.more-actions')}
              />
            }
          >
            {member.answersCount && (
              <MenuItem
                onClick={handleViewAnswers}
                content={t(
                  'groups-web.members.more-actions.view-answers.menu-item',
                )}
              />
            )}
            {!isMemberAdmin && (
              <MenuItem
                onClick={handleAddAdminRole}
                content={t(
                  'groups-web.members.more-actions.add-admin-role.menu-item',
                )}
              />
            )}
            {isMemberAdmin && (
              <MenuItem
                onClick={handleRemoveAdminRole}
                content={t(
                  'groups-web.members.more-actions.remove-admin-role.menu-item',
                )}
              />
            )}
            <MenuItem
              onClick={handleRemove}
              content={t(
                'groups-web.members.more-actions.remove-member-from-group.menu-item',
              )}
            />
          </Menu>
        </Show>
      </ProfileListItem>

      <PromoteMemberDialog
        groupId={groupId}
        member={member}
        isOpen={dialogs.changeRole}
        onClose={handleDialogClose('changeRole')}
      />

      <RemoveMemberDialog
        groupId={groupId}
        member={member}
        isOpen={dialogs.remove}
        onClose={handleDialogClose('remove')}
      />

      <MemberAnswersDialog
        groupId={groupId}
        member={member}
        isOpen={dialogs.answers}
        onClose={handleDialogClose('answers')}
      />
    </>
  );

  function handleRemove() {
    showDialog('remove');
    reportBi('remove_member', member.siteMemberId);
  }

  function handleAddAdminRole() {
    showDialog('changeRole');
    reportBi('set_admin', member.siteMemberId);
  }

  function handleRemoveAdminRole() {
    showDialog('changeRole');
    reportBi('remove_admin', member.siteMemberId);
  }

  function handleViewAnswers() {
    showDialog('answers');
  }

  function showDialog(dialog: keyof typeof dialogs) {
    setDialogs((dialogs) => ({ ...dialogs, [dialog]: true }));
  }

  function handleDialogClose(dialog: keyof typeof dialogs) {
    return () => setDialogs((dialogs) => ({ ...dialogs, [dialog]: false }));
  }

  function reportBi(action: string, memberId?: string) {
    bi.report(
      memberTabAdminActionClick({
        action,
        group_id: groupId,
        origin: 'member_tab_member_option_clk',
        site_member_id: memberId,
      }),
    );
  }
}

MemberListItem.displayName = 'MemberListItem';

export function MemberListItemSkeleton() {
  const { isMobile } = useEnvironment();

  return (
    <ListItem disablePadding>
      <ListItemAvatar>
        <Skeleton
          variant="circular"
          width={isMobile ? 36 : 52}
          height={isMobile ? 36 : 52}
        />
      </ListItemAvatar>
      <ListItemText title={<Skeleton width={isMobile ? '60%' : '30%'} />} />
    </ListItem>
  );
}

MemberListItemSkeleton.displayName = 'MemberListItemSkeleton';
